import { z } from 'zod';
import { CurrencyCodeSchema, DateOnlySchema, IdSchema, ListQuerySchema, MoneyAmountSchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
const ContactFieldsSchema = z.strictObject({
    email: z.email().nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
});
export const CompanyStatusSchema = z.enum(['active', 'inactive', 'prospect']);
export const CompanySchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    name: z.string(),
    status: CompanyStatusSchema,
    website: z.url().nullable(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    countryCode: z.string().length(2).nullable(),
    notes: z.string().nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateCompanyRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(200),
    status: CompanyStatusSchema.default('active'),
    website: z.url().nullable().default(null),
    email: z.email().nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    addressLine1: z.string().trim().max(200).nullable().default(null),
    addressLine2: z.string().trim().max(200).nullable().default(null),
    city: z.string().trim().max(100).nullable().default(null),
    region: z.string().trim().max(100).nullable().default(null),
    postalCode: z.string().trim().max(30).nullable().default(null),
    countryCode: z.string().trim().length(2).toUpperCase().nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
});
export const UpdateCompanyRequestSchema = CreateCompanyRequestSchema.partial().extend({
    version: VersionSchema,
});
export const ContactSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    companyId: IdSchema.nullable(),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string().nullable(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    notes: z.string().nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateContactRequestSchema = z
    .strictObject({
    companyId: IdSchema.nullable().default(null),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    title: z.string().trim().max(150).nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
})
    .extend(ContactFieldsSchema.shape);
export const UpdateContactRequestSchema = CreateContactRequestSchema.partial().extend({
    version: VersionSchema,
});
export const LeadStatusSchema = z.enum([
    'new',
    'contacted',
    'qualified',
    'disqualified',
    'converted',
]);
export const LeadSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    companyName: z.string().nullable(),
    contactName: z.string(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    source: z.string().nullable(),
    status: LeadStatusSchema,
    ownerMembershipId: IdSchema.nullable(),
    notes: z.string().nullable(),
    /** Every lead has exactly one job file; the job file owns property and customer context. */
    jobFileId: IdSchema,
    starred: z.boolean(),
    pausedUntil: DateOnlySchema.nullable(),
    archivedAt: TimestampSchema.nullable(),
    convertedAt: TimestampSchema.nullable(),
    convertedOpportunityId: IdSchema.nullable(),
    convertedProjectId: IdSchema.nullable(),
    clonedFromLeadId: IdSchema.nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const LeadListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    status: LeadStatusSchema.optional(),
    source: z.string().trim().max(100).optional(),
    ownerMembershipId: IdSchema.optional(),
    starred: z.stringbool().optional(),
    includeArchived: z.stringbool().default(false),
    includePaused: z.stringbool().default(true),
    createdFrom: DateOnlySchema.optional(),
    createdTo: DateOnlySchema.optional(),
});
export const LeadVersionCommandSchema = z.strictObject({ version: VersionSchema });
export const PauseLeadCommandSchema = z.strictObject({
    until: DateOnlySchema,
    version: VersionSchema,
});
/** Cloning never copies conversion links, claim identity, or private history. */
export const CloneLeadCommandSchema = z.strictObject({
    reuseProperty: z.boolean(),
    version: VersionSchema,
});
/** What a conversion will create and reuse, for the confirmation step (specification 8.5). */
export const LeadConversionPreviewSchema = z.strictObject({
    leadId: IdSchema,
    jobFileId: IdSchema,
    jobFileReferenceNumber: z.string(),
    jobFileLifecycle: z.string(),
    propertyId: IdSchema.nullable(),
    customerAccountId: IdSchema.nullable(),
    lossEventId: IdSchema.nullable(),
    insuranceClaimId: IdSchema.nullable(),
    documentCount: z.number().int().nonnegative(),
    alreadyConverted: z.boolean(),
    warnings: z.array(z.string()),
});
const ConvertProjectSchema = z.strictObject({
    name: z.string().trim().min(1).max(200),
    code: z.string().trim().max(50).nullable().default(null),
    statusDefinitionId: IdSchema.nullable().default(null),
    workCategoryIds: z.array(IdSchema).max(20).default([]),
    ownerMembershipId: IdSchema.nullable().default(null),
});
const ConvertOpportunitySchema = z.strictObject({
    name: z.string().trim().min(1).max(200),
    amount: MoneyAmountSchema.nullable().default(null),
    currency: CurrencyCodeSchema.default('USD'),
    companyId: IdSchema.nullable().default(null),
    primaryContactId: IdSchema.nullable().default(null),
});
/** Idempotent: the same key returns the first result; a different key after conversion conflicts. */
export const ConvertLeadCommandSchema = z
    .strictObject({
    idempotencyKey: z.string().trim().min(8).max(200),
    project: ConvertProjectSchema.optional(),
    opportunity: ConvertOpportunitySchema.optional(),
    /** Fills missing job file context before conversion; never overwrites existing context. */
    propertyId: IdSchema.nullable().default(null),
    customerAccountId: IdSchema.nullable().default(null),
    version: VersionSchema,
})
    .refine((value) => value.project !== undefined || value.opportunity !== undefined, {
    message: 'Convert to a project, an opportunity, or both',
    path: ['project'],
});
export const LeadConversionResultSchema = z.strictObject({
    leadId: IdSchema,
    jobFileId: IdSchema,
    projectId: IdSchema.nullable(),
    opportunityId: IdSchema.nullable(),
    replayed: z.boolean(),
});
export const CreateLeadRequestSchema = z.strictObject({
    companyName: z.string().trim().max(200).nullable().default(null),
    contactName: z.string().trim().min(1).max(200),
    email: z.email().nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    source: z.string().trim().max(100).nullable().default(null),
    status: LeadStatusSchema.default('new'),
    ownerMembershipId: IdSchema.nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
    /** Intake context recorded on the new job file, never copied onto the lead. */
    propertyId: IdSchema.nullable().default(null),
    customerAccountId: IdSchema.nullable().default(null),
});
export const UpdateLeadRequestSchema = CreateLeadRequestSchema.omit({
    propertyId: true,
    customerAccountId: true,
})
    .partial()
    .extend({
    version: VersionSchema,
});
export const OpportunityStageSchema = z.enum([
    'discovery',
    'qualified',
    'proposal',
    'negotiation',
    'won',
    'lost',
]);
export const OpportunitySchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    companyId: IdSchema.nullable(),
    primaryContactId: IdSchema.nullable(),
    name: z.string(),
    stage: OpportunityStageSchema,
    amount: MoneyAmountSchema.nullable(),
    currency: CurrencyCodeSchema,
    probability: z.number().int().min(0).max(100),
    expectedCloseDate: DateOnlySchema.nullable(),
    ownerMembershipId: IdSchema.nullable(),
    notes: z.string().nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateOpportunityRequestSchema = z.strictObject({
    companyId: IdSchema.nullable().default(null),
    primaryContactId: IdSchema.nullable().default(null),
    name: z.string().trim().min(1).max(200),
    stage: OpportunityStageSchema.default('discovery'),
    amount: MoneyAmountSchema.nullable().default(null),
    currency: CurrencyCodeSchema.default('USD'),
    probability: z.number().int().min(0).max(100).default(0),
    expectedCloseDate: DateOnlySchema.nullable().default(null),
    ownerMembershipId: IdSchema.nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
});
export const UpdateOpportunityRequestSchema = CreateOpportunityRequestSchema.partial().extend({
    version: VersionSchema,
});
export const CompanyListSchema = z.strictObject({
    items: z.array(CompanySchema),
    page: PageInfoSchema,
});
export const ContactListSchema = z.strictObject({
    items: z.array(ContactSchema),
    page: PageInfoSchema,
});
export const LeadListSchema = z.strictObject({ items: z.array(LeadSchema), page: PageInfoSchema });
export const OpportunityListSchema = z.strictObject({
    items: z.array(OpportunitySchema),
    page: PageInfoSchema,
});
